// File: src/components/home/HomeComponent.js
// Purpose: Home page component - converted to functional component with hooks
// Last modified: 2025-11-06

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MobileNavigation from '../BottomNavigation';
import PackagesList from '../packages/PackagesList';
import AppDownloadBanner from '../AppDownloadBanner';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import TuneIcon from '@mui/icons-material/Tune';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Sidebar } from 'primereact/sidebar';
import { Fieldset } from 'primereact/fieldset';
import {
    selectPackages,
    selectPackagesLoading
} from '../../store/slices/packagesSlice';
import {
    selectSorting,
    selectFilter,
    updateSorting,
    updateFilter
} from '../../store/slices/userSlice';
import './home.scss';
import '../package/skeleton.scss';

// Constants
const FILTER_OPTIONS = [
    { name: 'Hammasi', code: 'all' },
    { name: 'Faqat ochiqlar', code: 'only_open' },
    { name: 'Faqat yopiqlar', code: 'only_closed' },
    { name: 'Faqat 30-savollik', code: 'only_normal' },
    { name: 'Faqat 10-savollik', code: 'only_10_questions' },
    { name: 'Faqat rasmlik', code: 'only_picture' },
];

const SORTING_OPTIONS = [
    { name: 'To\'plam nomi: A-Z', code: 'name_a_z' },
    { name: 'To\'plam nomi: Z-A', code: 'name_z_a' },
    { name: 'Sana: Eski', code: 'date_a_z' },
    { name: 'Sana: Yangi', code: 'date_z_a' }
];

/**
 * Sort packages based on selected option
 */
const sortPackages = (packages, sortOption) => {
    const sorted = [...packages];

    switch (sortOption) {
        case 'name_a_z':
            return sorted.sort((a, b) => a.position - b.position);
        case 'name_z_a':
            return sorted.sort((a, b) => b.position - a.position);
        case 'date_a_z':
            return sorted.sort((a, b) =>
                new Date(a.createdDate) - new Date(b.createdDate)
            );
        case 'date_z_a':
            return sorted.sort((a, b) =>
                new Date(b.createdDate) - new Date(a.createdDate)
            );
        default:
            return sorted;
    }
};

/**
 * Filter packages based on selected option
 */
const filterPackages = (packages, filterOption) => {
    if (filterOption === 'all') {
        return packages;
    }

    return packages.filter(p => {
        switch (filterOption) {
            case 'only_open':
                return p.isPackageFree;
            case 'only_closed':
                return !p.isPackageFree;
            case 'only_normal':
                return p.type === 'NORMAL';
            case 'only_10_questions':
                return p.type === 'TEN_QUESTION';
            case 'only_picture':
                return p.numberOfPictureQuestions > 0;
            default:
                return true;
        }
    });
};

/**
 * HomeComponent
 * Main home page with package listing, filtering, and sorting
 */
const HomeComponent = () => {
    const dispatch = useDispatch();

    // Redux state
    const packages = useSelector(selectPackages);
    const loading = useSelector(selectPackagesLoading);
    const userSelectedSorting = useSelector(selectSorting);
    const userSelectedFilter = useSelector(selectFilter);

    // Local state
    const [showSortAndFilterSidebar, setShowSortAndFilterSidebar] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

    // Get current sorting and filter options
    const selectedSortOption = useMemo(() =>
            SORTING_OPTIONS.find(s => s.code === userSelectedSorting) || SORTING_OPTIONS[0],
        [userSelectedSorting]
    );

    const selectedFilterOption = useMemo(() =>
            FILTER_OPTIONS.find(f => f.code === userSelectedFilter) || FILTER_OPTIONS[0],
        [userSelectedFilter]
    );

    // Filter and sort packages
    const visiblePackages = useMemo(() => {
        const filtered = filterPackages(packages, selectedFilterOption.code);
        return sortPackages(filtered, selectedSortOption.code);
    }, [packages, selectedFilterOption, selectedSortOption]);

    // Calculate statistics
    const packageStats = useMemo(() => {
        const normalCount = visiblePackages.filter(p => p.type === 'NORMAL').length;
        const tenQuestionCount = visiblePackages.filter(p => p.type === 'TEN_QUESTION').length;
        const numberOfQuestions = (normalCount * 30) + (tenQuestionCount * 10);

        return {
            totalPackages: visiblePackages.length,
            numberOfQuestions
        };
    }, [visiblePackages]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1200);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Event handlers
    const handleSortChange = (event) => {
        const selectedOption = event.target.value;
        dispatch(updateSorting(selectedOption));
    };

    const handleFilterChange = (event) => {
        const selectedOption = event.target.value;
        dispatch(updateFilter(selectedOption));
    };

    // Render methods
    const renderHeader = () => (
        <div className={'header'}>
            <svg xmlns='http://www.w3.org/2000/svg'
                 xmlnsXlink='http://www.w3.org/1999/xlink'
                 width='500' zoomAndPan='magnify' viewBox='0 0 375 374.999991'
                 height='500' preserveAspectRatio='xMidYMid meet' version='1.0'
                 className='logo-svg'>
                <defs>
                    <g/>
                    <clipPath id='id1'>
                        <path d='M 28.875 28.875 L 346.125 28.875 L 346.125 346.125 L 28.875 346.125 Z M 28.875 28.875 ' clipRule='nonzero'/>
                    </clipPath>
                </defs>
                <rect x='-37.5' width='450' y='-37.499999' height='449.999989' fillOpacity='1'/>
                <rect x='-37.5' width='450' y='-37.499999' height='449.999989' fillOpacity='1'/>
                <rect x='-37.5' width='450' y='-37.499999' height='449.999989' fillOpacity='1'/>
                <rect x='-37.5' width='450' y='-37.499999' height='449.999989' fillOpacity='1'/>
                <g clipPath='url(#id1)'>
                    <path fill='#03989e'
                          d='M 187.5 28.875 C 100.035156 28.875 28.875 100.035156 28.875 187.5 C 28.875 274.96875 100.035156 346.125 187.5 346.125 C 274.964844 346.125 346.125 274.96875 346.125 187.5 C 346.125 100.035156 274.964844 28.875 187.5 28.875 Z M 187.5 341.992188 C 102.3125 341.992188 33.011719 272.683594 33.011719 187.503906 C 33.011719 102.316406 102.3125 33.011719 187.5 33.011719 C 272.683594 33.011719 341.988281 102.316406 341.988281 187.503906 C 341.992188 272.6875 272.683594 341.992188 187.5 341.992188 Z M 187.5 341.992188 '
                          fillOpacity='1' fillRule='nonzero'/>
                </g>
                <path fill='#03989e'
                      d='M 187.5 41.257812 C 106.859375 41.257812 41.257812 106.863281 41.257812 187.5 C 41.257812 268.140625 106.859375 333.742188 187.5 333.742188 C 268.140625 333.742188 333.738281 268.136719 333.738281 187.5 C 333.738281 106.863281 268.136719 41.257812 187.5 41.257812 Z M 187.5 41.257812 '
                      fillOpacity='1' fillRule='nonzero'/>
                <g fillOpacity='1'>
                    <g transform='translate(116.739263, 277.874982)'>
                        <g>
                            <path fill='white'
                                  d='M 103.28125 -171.65625 C 97.507812 -171.84375 86.710938 -171.9375 70.890625 -171.9375 L 44.109375 -171.65625 C 42.617188 -171.46875 40.195312 -171.375 36.84375 -171.375 C 33.675781 -168.59375 30.929688 -164.269531 28.609375 -158.40625 C 26.285156 -152.539062 24.566406 -146.445312 23.453125 -140.125 L 17.578125 -139.84375 C 15.347656 -139.84375 13.769531 -139.9375 12.84375 -140.125 L 9.484375 -180.875 L 16.75 -187.578125 C 40.007812 -186.640625 60.664062 -186.171875 78.71875 -186.171875 C 89.695312 -186.171875 107.742188 -186.640625 132.859375 -187.578125 L 135.375 -176.40625 L 36.28125 -14.515625 L 68.9375 -14.234375 L 104.671875 -14.515625 C 113.421875 -24.929688 119.5625 -36.929688 123.09375 -50.515625 C 125.320312 -50.890625 127.273438 -51.078125 128.953125 -51.078125 C 130.441406 -51.078125 132.488281 -50.890625 135.09375 -50.515625 L 132.578125 0.84375 C 119.742188 0.28125 96.484375 0 62.796875 0 C 49.585938 0 38.003906 0.140625 28.046875 0.421875 C 18.097656 0.703125 11.257812 0.929688 7.53125 1.109375 L 6.703125 -12.84375 Z M 103.28125 -171.65625 '/>
                        </g>
                    </g>
                </g>
            </svg>
            <span>Zakovat savollari</span>
        </div>
    );

    const renderFilterSection = () => {
        const isFilterApplied = selectedFilterOption.code !== 'all';

        return (
            <div className={'filter-container'}>
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2} columns={16}>
                        <Grid size={14} className={'stats'}>
                            <Grid size={16} className={'results'}>
                                <Grid container spacing={2} columns={16}>
                                    <Grid size={8}>
                                        To'plamlar soni: {packageStats.totalPackages}
                                    </Grid>
                                    <Grid size={8} className={'questions'}>
                                        Savollar soni: {packageStats.numberOfQuestions}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} columns={16} style={{marginTop: '20px'}}>
                        <Grid size={8}
                              onClick={() => setShowSortAndFilterSidebar(true)}
                              className={'sorting'}>
                            <Fieldset legend='Saralash'>
                                <p className='m-0'>
                                    {selectedSortOption.name}
                                </p>
                            </Fieldset>
                        </Grid>
                        <Grid size={8}
                              className={'filter'}
                              onClick={() => setShowSortAndFilterSidebar(true)}>
                            <div>
                                <TuneIcon className={'filter-icon'} />
                                <span>
                                    Filter
                                    {isFilterApplied && <span>*</span>}
                                </span>
                            </div>
                        </Grid>
                    </Grid>
                </Box>
            </div>
        );
    };

    const sidebarPosition = isMobile ? 'bottom' : 'right';
    const customHeader = (
        <div className="flex align-items-center gap-2">
            <h3>Saralash</h3>
        </div>
    );

    return (
        <div className={'home'}>
            {renderHeader()}
            {renderFilterSection()}

            <PackagesList packages={visiblePackages} loading={loading} />

            <AppDownloadBanner />

            <Sidebar
                visible={showSortAndFilterSidebar}
                position={sidebarPosition}
                onHide={() => setShowSortAndFilterSidebar(false)}
                header={customHeader}
            >

                <div className={'options'}>
                    <RadioGroup
                        aria-labelledby='demo-radio-buttons-group-label'
                        value={selectedSortOption.code}
                        onChange={handleSortChange}
                        name='radio-buttons-group'
                    >
                        {SORTING_OPTIONS.map(option => (
                            <FormControlLabel
                                key={option.code}
                                value={option.code}
                                control={<Radio />}
                                label={option.name}
                            />
                        ))}
                    </RadioGroup>
                </div>
                <h3>Filter</h3>
                <div className={'options'}>
                    <RadioGroup
                        aria-labelledby='demo-radio-buttons-group-label'
                        value={selectedFilterOption.code}
                        onChange={handleFilterChange}
                        name='radio-buttons-group'
                    >
                        {FILTER_OPTIONS.map(option => (
                            <FormControlLabel
                                key={option.code}
                                value={option.code}
                                control={<Radio />}
                                label={option.name}
                            />
                        ))}
                    </RadioGroup>
                </div>
            </Sidebar>

            <MobileNavigation />
        </div>
    );
};

export default HomeComponent;
